import { useState, useEffect, useRef, useCallback } from 'react';
import { PopoverOrigin } from '@fhl-world/salmon-ui';

type PopoverPosition = {
	anchorOrigin: PopoverOrigin;
	transformOrigin?: PopoverOrigin;
};

const basePosition: PopoverPosition = {
	anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
};

const aboveInputPosition: PopoverPosition = {
	anchorOrigin: { vertical: 'top', horizontal: 'left' },
	transformOrigin: { vertical: 'bottom', horizontal: 'left' },
};

const HEIGHT_BETWEEN_INPUT_AND_SUGGESTION = 12;

export const useGetPopoverPosition = (anchorEl: HTMLElement | null) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const suggestRef = useRef<HTMLDivElement>(null);
	const updateTimeoutRef = useRef<NodeJS.Timeout>();

	const [position, setPosition] = useState<PopoverPosition>(basePosition);
	const [updateTrigger, setUpdateTrigger] = useState(0);

	const updatePosition = useCallback(() => {
		if (!anchorEl) {
			setPosition(basePosition);
			return;
		}

		if (!inputRef.current || !suggestRef.current) {
			return;
		}

		const topOfSuggest =
			suggestRef.current.getBoundingClientRect().top +
			HEIGHT_BETWEEN_INPUT_AND_SUGGESTION;

		const bottomOfInput = inputRef.current.getBoundingClientRect().bottom;

		if (topOfSuggest < bottomOfInput) {
			setPosition(aboveInputPosition);
		} else {
			setPosition(basePosition);
		}
	}, [anchorEl]);

	useEffect(() => {
		updatePosition();
	}, [updatePosition, updateTrigger]);

	useEffect(() => {
		if (!anchorEl) return;

		const handleScroll = () => {
			if (updateTimeoutRef.current) {
				clearTimeout(updateTimeoutRef.current);
			}
			updateTimeoutRef.current = setTimeout(() => {
				setUpdateTrigger((prev) => prev + 1);
			}, 10);
		};

		const scrollableParents: (Element | Window)[] = [window];
		let element: HTMLElement | null = anchorEl;

		while (element && element !== document.body) {
			if (
				element.scrollHeight > element.clientHeight ||
				element.scrollWidth > element.clientWidth
			) {
				scrollableParents.push(element);
			}
			element = element.parentElement;
		}

		scrollableParents.forEach((parent) => {
			parent.addEventListener('scroll', handleScroll, { passive: true });
		});

		window.addEventListener('touchmove', handleScroll, { passive: true });

		return () => {
			if (updateTimeoutRef.current) {
				clearTimeout(updateTimeoutRef.current);
			}
			scrollableParents.forEach((parent) => {
				parent.removeEventListener('scroll', handleScroll);
			});
			window.removeEventListener('touchmove', handleScroll);
		};
	}, [anchorEl, updatePosition]);

	return { inputRef, suggestRef, position };
};
